import { z } from "zod";

const hubKindKey = z
	.string()
	.trim()
	.min(1, "Enter a type code.")
	.max(40)
	.regex(/^[a-z0-9_-]+$/, "Use lowercase letters, numbers, hyphens or underscores.");

const hubWeekday = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

export const hubLeavePolicyRowSchema = z.object({
	kind: hubKindKey,
	days: z.number().int().min(0).max(366),
	paid: z.boolean(),
	approval: z.boolean(),
});

export const hubClaimPolicyRowSchema = z.object({
	kind: hubKindKey,
	limit: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "Enter a limit."),
	approval: z.boolean(),
});

export const hubAttendanceShiftSchema = z.object({
	id: z.string().trim().min(1).max(40),
	name: z.string().trim().min(1, "Enter a shift name.").max(80),
	start: z.string().trim().max(8),
	end: z.string().trim().max(8),
});

export const hubAttendanceSettingsSchema = z.object({
	lateMinutes: z.number().int().min(0).max(480),
	shifts: z.array(hubAttendanceShiftSchema).max(20),
});

export const hubPayrollSettingsSchema = z.object({
	payDay: z.number().int().min(1).max(31),
	epf: z.number().min(0).max(100),
	socso: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "Enter a SOCSO amount."),
	eis: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "Enter an EIS amount."),
});

export const hubAttendanceSlotSchema = z.object({
	id: z.number().int().positive().optional(),
	mine: z.boolean().optional(),
	person: z.string().trim().min(1, "Enter a person.").max(80),
	days: z.array(hubWeekday).min(1, "Choose at least one day.").max(7),
	shift: z.string().trim().min(1).max(40),
});

export const updateHubPeopleSettingsSchema = z
	.object({
		leavePolicy: z.array(hubLeavePolicyRowSchema).max(40).optional(),
		claimPolicy: z.array(hubClaimPolicyRowSchema).max(40).optional(),
		attendanceSettings: hubAttendanceSettingsSchema.optional(),
		payrollSettings: hubPayrollSettingsSchema.optional(),
		attendanceSlots: z.array(hubAttendanceSlotSchema).max(200).optional(),
	})
	.superRefine((value, ctx) => {
		const touched =
			value.leavePolicy !== undefined
			|| value.claimPolicy !== undefined
			|| value.attendanceSettings !== undefined
			|| value.payrollSettings !== undefined
			|| value.attendanceSlots !== undefined;
		if (!touched) {
			ctx.addIssue({ code: "custom", message: "Nothing to save." });
		}
		if (value.leavePolicy) {
			const kinds = new Set<string>();
			for (const row of value.leavePolicy) {
				if (kinds.has(row.kind)) {
					ctx.addIssue({ code: "custom", message: "Each leave type must be unique." });
					break;
				}
				kinds.add(row.kind);
			}
		}
		if (value.claimPolicy) {
			const kinds = new Set<string>();
			for (const row of value.claimPolicy) {
				if (kinds.has(row.kind)) {
					ctx.addIssue({ code: "custom", message: "Each claim type must be unique." });
					break;
				}
				kinds.add(row.kind);
			}
		}
	});

export type UpdateHubPeopleSettingsInput = z.infer<typeof updateHubPeopleSettingsSchema>;
